import React from 'react';
import AloSidebar from 'src/scripts/templates/_shared/ui-components/YogaUI/alo-ui/AloSidebar';
import { StoreLocatorTile } from './StoreLocatorTile';
import { pluralize } from 'src/scripts/utils/helpers';
import { observer } from 'mobx-react-lite';
import { closeStoreLocatorSidebar, storeLocatorSidebarState } from './storeLocatorSidebarState';
import { StoreLocatorForm } from './StoreLocatorForm';
import { IStoreAvailabilityData } from './storeLocatorTypes';
import { ShippingCountryState } from '../../../../utils/shipping';
import { customerLocationState } from './customerLocationState';
import { LoadingMoreSpinner } from 'src/scripts/templates/product-listing-page/components/SmallUIComponents';
import { findInStoreState } from '../FindInStore/findInStoreState';
import { bopisState } from '../Bopis/bopisState';
import { SiteSettings } from 'src/scripts/utils/environment';
import './style.scss';

let StoreLocatorSidebar = () => {
  const postalCodeToDisplay = customerLocationState.zip !== null ? customerLocationState.zip : 'you';

  if (!storeLocatorSidebarState.data) {
    return <SidebarWithLoading />;
  }

  const {
    product,
    userSelectedSize,
    storeSelectionEnabled,
    storeLocatorType,
    selectedVariant,
    isAvailableOnline,
    page,
  } = storeLocatorSidebarState.data;

  const storesData =
    storeLocatorType === 'fis'
      ? findInStoreState.storesData
      : page === 'pdp'
      ? bopisState.pdpStoresData
      : bopisState.cartStoresData;

  const error =
    storeLocatorType === 'fis'
      ? findInStoreState.storesDataError
      : page === 'pdp'
      ? bopisState.pdpStoresDataError
      : bopisState.cartStoresDataError;

  const errorMessage = SiteSettings?.bopisErrorMessage;
  const pricingDisclaimer = SiteSettings?.findInStorePricingDisclaimer;
  const noResultsCopy =
    storeLocatorType === 'fis'
      ? `Sorry, there are no ALO stores found near you. Try a different ${
          ShippingCountryState.countryCode.toLowerCase() === 'us' ? 'zip' : 'postal'
        } code.`
      : 'No stores found near you. Try a different zip code or have it shipped.';

  const shouldShowStoreLocatorForm =
    page === 'cart' && bopisState.cartStoresDataError?.detail.toLowerCase().includes('location is unknown');

  return (
    <>
      {storesData ? (
        <AloSidebar
          title={storeLocatorSidebarState.title}
          headerBorder={true}
          visible={storeLocatorSidebarState.isOpen}
          onClose={() => closeStoreLocatorSidebar()}
          desktopWidth={730}
        >
          <div className="store-locator-sidebar">
            {product ? (
              <div className="store-locator-sidebar-product">
                {product.images && (
                  <div className="store-locator-product-image">
                    <img src={product?.images[0] ? product?.images[0] : product?.images[1]} alt={product.title}></img>
                  </div>
                )}
                <div className="store-locator-sidebar-product-text">
                  {product.baseTitle && <h4 className="store-locator-product-title">{product.baseTitle}</h4>}
                  <div className="store-locator-product-variants">
                    {product.colorName && (
                      <p>
                        <span>Color:</span> {product.colorName}
                      </p>
                    )}
                    {userSelectedSize && (
                      <p>
                        <span>Size:</span> {userSelectedSize}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
            <div className="store-locator-sidebar-form">
              <h5 className="h5">Check Nearby Store Inventory</h5>
              <StoreLocatorForm storeLocatorType={storeLocatorType} selectedVariant={selectedVariant} page={page} />
              {page === 'cart' ? (
                <div className="cart-availability">
                  <p>Availability for all items in your bag</p>
                  <svg className="icon-bag" xmlns="http://www.w3.org/2000/svg" width={18} height={24}>
                    <g>
                      <path
                        className="bag-path-static"
                        d="M17.54 5.424a.47.47 0 0 1 .46.474v17.627a.47.47 0 0 1-.46.475H.46a.47.47 0 0 1-.46-.475V5.898a.47.47 0 0 1 .46-.474h4.795v-1.56C5.255 1.733 6.935 0 9 0c2.065 0 3.745 1.733 3.745 3.864v1.56zm-11.365 0h5.64v-1.56c0-1.608-1.264-2.915-2.82-2.915-1.555 0-2.82 1.307-2.82 2.915zm10.905.949h-4.335V8.61a.47.47 0 0 1-.46.475.47.47 0 0 1-.46-.475V6.373h-5.65V8.61a.47.47 0 0 1-.46.475.47.47 0 0 1-.46-.475V6.373H.92V23.05h16.16z"
                      />
                    </g>
                  </svg>
                </div>
              ) : null}
              {storesData && storesData.length > 0 ? (
                <>
                  <div className="store-locator-sidebar-results">
                    <p className="store-locator-sidebar-results__store-count">
                      {pluralize(storesData.length, `store`)} found near {postalCodeToDisplay}
                    </p>
                    {pricingDisclaimer && storeLocatorType === 'fis' ? (
                      <p className="store-locator-pricing-disclaimer black-text">{pricingDisclaimer}</p>
                    ) : null}
                  </div>
                  <div className="store-locator-tile-sidebar-container">
                    {storesData.map((storeAvailabilityData: IStoreAvailabilityData) => (
                      <StoreLocatorTile
                        key={storeAvailabilityData.location_id}
                        store={storeAvailabilityData}
                        // cart will always show original copy
                        isAvailableOnline={page === 'cart' ? true : Boolean(isAvailableOnline)}
                        showFullTile={true}
                        inputName="sidebar"
                        storeSelectionEnabled={storeSelectionEnabled}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <p className="store-locator-no-results">{error ? errorMessage : noResultsCopy}</p>
              )}
            </div>
          </div>
        </AloSidebar>
      ) : shouldShowStoreLocatorForm ? (
        <AloSidebar
          title={storeLocatorSidebarState.title}
          headerBorder={true}
          visible={storeLocatorSidebarState.isOpen}
          onClose={() => closeStoreLocatorSidebar()}
          desktopWidth={730}
        >
          <div className="store-locator-sidebar">
            <StoreLocatorForm storeLocatorType={storeLocatorType} selectedVariant={selectedVariant} page={page} />
          </div>
        </AloSidebar>
      ) : (
        <SidebarWithLoading />
      )}
    </>
  );
};

StoreLocatorSidebar = observer(StoreLocatorSidebar);

export { StoreLocatorSidebar };

const SidebarWithLoading = () => (
  <AloSidebar
    title={storeLocatorSidebarState.title}
    headerBorder={true}
    visible={storeLocatorSidebarState.isOpen}
    onClose={() => closeStoreLocatorSidebar()}
    desktopWidth={730}
  >
    <div className="store-locator-sidebar">
      <LoadingMoreSpinner />
    </div>
  </AloSidebar>
);
